import React, { useEffect, } from 'react';
import { Button, } from 'reactstrap';
import { keyBy, orderBy, isEmpty, omit, groupBy, } from 'lodash';
import numeral from 'numeral';
import fileDownload from 'js-file-download';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import { format as formatDate, addMilliseconds, } from 'date-fns';
import { toast } from 'react-toastify';

import env from '../../env';
import firebase from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { getCollectionData, } from '../../shared/firebase';
import { newId, createProductsWithSkuCheck } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import Page from '../hocs/Page';
import SetFormModal from '../modals/SetFormModal';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ImportButton from '../ImportButton';
import QuerySortLabel from '../QuerySortLabel';
import QueryBoolean from '../QueryBoolean';
import DataToggle from '../DataToggle';
import ProductsTabs from '../ProductsTabs';
import ProductTagsDisplay from '../ProductTagsDisplay';

const { entries } = Object;
const db = firebase.firestore();
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
};

export default Page(function Sets (props) {
  const { user, tenant, } = props;
  const {
    showsAll,
    sort = { field: 'id', direction: 'desc', },
  } = useQueryParams();
  const productsRef = tenant.ref.collection('products');
  const shippingRequestsRef = tenant.ref.collection('shippingRequests');
  const shippingRequests = useCollectionSubscription(shippingRequestsRef.where('status', 'not-in', ['shipped', 'discarded', 'cancelled']).orderBy('status'), [tenant.id]);
  const { items: productChunks, isLoading: isLoadingProductChunks } = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id], { detail: true });
  const chunkProducts = productChunks.flatMap(_ => entries(omit(_, ['id', 'ref', 'exists'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, })));
  const { items: products, } = useCollectionSubscription(productsRef.where('isSet', '==', true), [tenant.id], { detail: true });
  const chunkProductsBySku = keyBy(chunkProducts, 'sku');
  // 構成品のSKU・商品名表示に使う
  const productsById = keyBy(chunkProducts, 'id');
  const sortedProductItems = orderBy(products, (sortIterateees[sort.field] || sort.field), sort.direction);
  const [lastProduct] = orderBy(chunkProducts, _ => parseInt(_.id.replace(/\D/g, ''), 10), 'desc');
  const newRef = !isLoadingProductChunks && productsRef.doc(newId(lastProduct));
  const processValues = _ => ({
    ..._,
    isSet: true,
    // セット品は実在庫を持たず、Amazonにも出さない
    isAmazonProduct: false,
    itemName: _.itemName || _.name.slice(0, 10),
    shippingSize: _.shippingSize || null,
    extraSellerId: null,
  });
  const onClickDownloadSampleCsv = () => {
    const data = [
      {
        SKU: 'sku_set_123456',
        商品名: 'Aを2つとBを3つのセット',
        品名: 'セット',
        構成品SKU: 'sku_a',
        構成品数量: 2,
      },
      {
        SKU: 'sku_set_123456',
        商品名: '',
        品名: '',
        構成品SKU: 'sku_b',
        構成品数量: 3,
      },
    ];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, 'セット品_サンプル.csv');
  };
  useEffect(() => {
    user.ref.set({ preferences: { productsTab: '/products', } }, { merge: true });
  }, []);
  const validate = async (rows, row, i) => {
    const {
      商品名: name,
      SKU: sku,
      品名: itemName,
      構成品SKU: itemSku,
      構成品数量: itemQuantity,
    } = row;
    const isSubRow = rows.slice(0, i).map(_ => _.SKU).includes(sku);
    const componentProduct = chunkProductsBySku[itemSku];
    const errors = [
      (!isSubRow && !name) && '商品名は必須です',
      chunkProductsBySku[sku] != null && 'SKUが他に存在します',
      (sku || '').match(/\s/) && 'SKUに空白やタブ等が含まれています',
      (itemName && itemName.length > 16) && '品名は16文字以内です',
      componentProduct == null && '存在しない構成品SKUです',
      componentProduct?.isSet && 'セット品を構成品にはできません',
      componentProduct?.isCase && 'ケースを構成品にはできません',
      componentProduct?.isAmazonProduct && 'Amazon商品を構成品にはできません',
      !(itemQuantity > 0) && '構成品数量は1以上で指定してください',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      name,
      sku,
      itemName,
      itemSku,
      itemQuantity: parseInt(itemQuantity, 10),
    }
  };
  const processRows = async (rows) => {
    const validatedRows = await Promise.all(rows.map(validate.bind(null, rows)));
    if(validatedRows.filter(_ => !isEmpty(_.errors)).length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map((row) => {
        return {
          ...row.source,
          エラー内容: row.errors.join('\n'),
        };
      })));
      fileDownload(content, 'エラー結果.csv');
      throw new Error();
    } else {
      const groupedBySkuEntries = Object.values(groupBy(validatedRows, 'sku'));
      const rowsForCreate = groupedBySkuEntries.map((group, i) => {
        const [row] = group;
        return {
          productId: (parseInt(newId(lastProduct), 10) + i).toString(),
          sku: row.sku,
          data: {
            ...omit(row, ['source', 'errors', 'itemSku', 'itemQuantity']),
            extraSellerId: null,
            itemName: row.itemName || row.name.slice(0, 10),
            isAmazonProduct: false,
            shippingSize: null,
            isSet: true,
            items: group.map(_ => ({ productId: chunkProductsBySku[_.itemSku].id, quantity: _.itemQuantity })),
            createdAt: addMilliseconds(new Date(), i),
          },
        };
      });
      await createProductsWithSkuCheck(db, productsRef, rowsForCreate);
    }
  };

  return (
    <div className="products container-fluid">
      <div className="p-4 bg-white my-4">
        <ProductsTabs activeTab="sets" products={products} shippingRequests={shippingRequests} tenant={tenant} />
        <div className="d-flex justify-content-start mb-1 gap-1 align-items-end">
          <Button onClick={onClickDownloadSampleCsv}>
            <span className="fas fa-download mr-1" />
            サンプルCSVダウンロード
          </Button>
          <ImportButton processRows={processRows} disabled={isLoadingProductChunks} />
          <AddButton
            label="セット品追加"
            itemRef={newRef}
            FormModal={SetFormModal}
            processValues={processValues}
            disabled={isLoadingProductChunks}
            beforeSave={async ({ id, ...values }) => {
              const data = { ...omit(processValues({ ...values }), ['id', 'ref']), createdAt: new Date(), };
              await createProductsWithSkuCheck(db, productsRef, [{ productId: id, sku: values.sku, data }]);
              return { skipDefaultSet: true };
            }}
            formProps={{
              tenant,
              chunkProducts,
            }}
          />
          <div className="ml-2">
            <QueryBoolean paramName="showsAll" label="非表示も表示" defaultValue={'0'} />
          </div>
        </div>
        <div className="overflow-auto">
          {
            sortedProductItems.length > 0 ? (
              <table className="table table-sm table-bordered sticky-table">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: 150 }}>
                      <QuerySortLabel field="sku">
                        SKU
                      </QuerySortLabel>
                      <br />
                      <QuerySortLabel field="id">
                        登録日
                      </QuerySortLabel>
                      <div className="small">商品ID</div>
                    </th>
                    <th style={{ minWidth: 200 }}>
                      <QuerySortLabel field="name">
                        商品名
                      </QuerySortLabel>
                      <br />
                      品名
                    </th>
                    <th style={{ minWidth: 380 }}>構成品</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    (showsAll === '1' ? sortedProductItems : sortedProductItems.filter(_ => !_.hides)).map((productItem) => {
                      const { id, ref, name, sku, itemName, items = [], createdAt, } = productItem;
                      const beforeWrite = async () => {
                        const [shippingRequest] = await getCollectionData(tenant.ref.collection('shippingRequests').where('productIds', 'array-contains', id).limit(1));
                        if(shippingRequest) {
                          toast.error('一度でも出荷があったセット品は処理できません。');
                          return false;
                        }
                      };

                      return (
                        <tr key={id}>
                          <td>
                            <div>
                              <span className="font-weight-bold">SKU:</span> {sku}
                            </div>
                            <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                            <div className="small">{idWithTenant(ref)}</div>
                            <ProductTagsDisplay product={productItem} productTags={[]} />
                            {user.dev && <a href={`https://console.firebase.google.com/u/0/project/${env('FIREBASE_PROJECT_ID')}/firestore/data/~2Ftenants~2F${tenant.id}~2Fproducts~2F${id}`} target="_blank" rel="noopener noreferrer">F</a>}
                          </td>
                          <td>
                            <div>
                              {name}
                            </div>
                            <div className="font-weight-bold">
                              {itemName}
                            </div>
                          </td>
                          <td>
                            <table className="mb-0 table table-sm table-borderless">
                              <tbody>
                                {
                                  items.map((item) => {
                                    const componentProduct = productsById[item.productId];
                                    return (
                                      <tr key={item.productId}>
                                        <td>
                                          {
                                            componentProduct != null ? (
                                              <div>
                                                <div><span className="font-weight-bold">{componentProduct.sku}</span></div>
                                                <div className="small text-muted">{componentProduct.name}</div>
                                              </div>
                                            ) : (
                                              <span className="text-danger">削除済み商品</span>
                                            )
                                          }
                                        </td>
                                        <td className="text-right text-nowrap align-middle">{numeral(item.quantity).format('0,0')}</td>
                                      </tr>
                                    );
                                  })
                                }
                              </tbody>
                            </table>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 text-nowrap">
                              <EditButton size="sm" processValues={processValues} itemRef={ref} FormModal={SetFormModal} formProps={{ tenant, chunkProducts, values: productItem, }} />
                              <DeleteButton size="sm" item={productItem} itemRef={ref} beforeDelete={beforeWrite} />
                              <DataToggle data={productItem} fieldName="hides" label="非表示" inputProps={{ style: { transform: 'initial' }, }} labelProps={{ className: 'small', }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                セット品は未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
